import { FC, useEffect, useCallback, useState, useRef } from "react";
import { useTranslations } from "use-intl";
import clsx from "clsx";
import useAction, { NewUserTaskExpiredInfo } from "../AccountMode/useAction";

import rewardHubGif from "@/nav-localized/assets/header/reward_hub_web_01C.gif";

const GIF_PLAY_COUNT = 3;

/**
 * Patch the NETSCAPE2.0 loop-count inside a GIF binary so the browser
 * plays it exactly `loopCount` times then stops on the last frame.
 */
const patchGifLoopCount = async (src: string, loopCount: number): Promise<string> => {
  try {
    const res = await fetch(src);
    const buf = await res.arrayBuffer();
    const bytes = new Uint8Array(buf);

    const sig = [0x4e, 0x45, 0x54, 0x53, 0x43, 0x41, 0x50, 0x45, 0x32, 0x2e, 0x30];
    for (let i = 0; i < bytes.length - 18; i++) {
      if (bytes[i] !== 0x21 || bytes[i + 1] !== 0xff || bytes[i + 2] !== 0x0b) continue;
      if (sig.some((b, j) => bytes[i + 3 + j] !== b)) continue;

      bytes[i + 16] = loopCount & 0xff;
      bytes[i + 17] = (loopCount >> 8) & 0xff;
      return URL.createObjectURL(new Blob([bytes], { type: "image/gif" }));
    }
  } catch (e) {
    console.error("Failed to patch GIF loop count:", e);
  }
  return src;
};

interface NewUserBannerProps {
  className?: string;
  localeLink: string;
}

const NewUserBanner: FC<NewUserBannerProps> = ({ className, localeLink }) => {
  const t = useTranslations("header");
  const { getNewUserTaskExpiredInfo } = useAction();
  const imgRef = useRef<HTMLImageElement>(null);
  const blobUrlRef = useRef("");

  const [showBanner, setShowBanner] = useState(false);
  const [gifSrc, setGifSrc] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getNewUserTaskExpiredInfo<{
          code: number;
          data: NewUserTaskExpiredInfo;
        }>();
        if (res.code !== 200) return;

        setShowBanner(!res.data.challenge_task_time_expired || !res.data.new_user_task_expired);
      } catch (error) {
        console.error("Failed to fetch new user task expired info:", error);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showBanner) return;

    let revoked = false;
    const originalSrc = typeof rewardHubGif === "string" ? rewardHubGif : (rewardHubGif as any).src;

    patchGifLoopCount(originalSrc, GIF_PLAY_COUNT).then((src) => {
      if (revoked) {
        if (src.startsWith("blob:")) URL.revokeObjectURL(src);
        return;
      }
      blobUrlRef.current = src;
      setGifSrc(src);
    });

    return () => {
      revoked = true;
      if (blobUrlRef.current.startsWith("blob:")) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = "";
      }
    };
  }, [showBanner]);

  useEffect(() => {
    return () => {
      if (imgRef.current) {
        imgRef.current.src = "";
      }
    };
  }, []);

  const handleClick = useCallback(() => {
    window?.reportTrack("Web_Heading_Newuser_Click", {});
  }, []);

  if (!showBanner || !gifSrc) return null;

  return (
    <a
      href={`${localeLink}/task-center`}
      onClick={handleClick}
      className={clsx(
        "inline-flex h-full shrink-0 cursor-pointer items-center justify-center",
        className
      )}
    >
      <img
        ref={imgRef}
        src={gifSrc}
        alt={t("newUsersExclusive")}
        className="block h-[32px] w-[32px] shrink-0 object-contain object-center"
        loading="eager"
      />
    </a>
  );
};

export default NewUserBanner;
